import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getUserTeams, 
  getInvoicesForReport, 
  formatCurrency, 
  formatDate 
} from '../services/firebaseService';
import { 
  Team, 
  Invoice, 
  DateRange 
} from '../types';
import './GeneralReport.css';

const GeneralReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid' | 'checked' | 'unchecked'>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      // Select all teams by default
      setSelectedTeams([]);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate, endDate;
    
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert('Selecione pelo menos uma equipe e defina o período.');
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error loading report data:', error);
      alert('Erro ao carregar dados do relatório.');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = 
        searchTerm === '' ||
        invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.pms.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.os.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = 
        statusFilter === 'all' ||
        (statusFilter === 'paid' && invoice.paid) ||
        (statusFilter === 'unpaid' && !invoice.paid) ||
        (statusFilter === 'checked' && invoice.checked) ||
        (statusFilter === 'unchecked' && !invoice.checked);

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  const reportSummary = useMemo(() => {
    const totalInvoices = filteredInvoices.length;
    const paidInvoices = filteredInvoices.filter(inv => inv.paid).length;
    const checkedInvoices = filteredInvoices.filter(inv => inv.checked).length;
    const totalValue = filteredInvoices.reduce((sum, inv) => sum + (inv.totalWithFactor || 0), 0);
    const paidValue = filteredInvoices.filter(inv => inv.paid).reduce((sum, inv) => sum + (inv.totalWithFactor || 0), 0);
    
    return {
      totalInvoices,
      paidInvoices,
      unpaidInvoices: totalInvoices - paidInvoices,
      checkedInvoices,
      uncheckedInvoices: totalInvoices - checkedInvoices,
      totalValue,
      paidValue,
      pendingValue: totalValue - paidValue
    };
  }, [filteredInvoices]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map(team => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const exportToCSV = () => {
    const headers = [
      'ID', 'Equipe', 'Projeto', 'Título', 'PMS', 'OS', 'Município', 
      'Data', 'Valor Total', 'Corrigido', 'Pago', 'Status'
    ].join(';');

    const csvData = filteredInvoices.map(invoice => [
      invoice.id,
      invoice.team,
      invoice.project,
      invoice.title,
      invoice.pms,
      invoice.os,
      invoice.city,
      formatDate(invoice.date),
      invoice.totalWithFactor || 0,
      invoice.checked ? 'Sim' : 'Não',
      invoice.paid ? 'Sim' : 'Não',
      invoice.paid ? 'Pago' : invoice.checked ? 'Corrigido' : 'Pendente'
    ].join(';')).join('\n');

    const csvContent = [headers, csvData].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-geral-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (invoice: Invoice) => {
    if (invoice.paid) return <span className="status-badge paid">Pago</span>;
    if (invoice.checked) return <span className="status-badge checked">Corrigido</span>;
    return <span className="status-badge pending">Pendente</span>;
  };

  return (
    <div className="general-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Geral</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button onClick={handleDeselectAllTeams} className="control-btn">
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="status-select"
              >
                <option value="all">Todos</option>
                <option value="paid">Pagos</option>
                <option value="unpaid">Não Pagos</option>
                <option value="checked">Corrigidos</option>
                <option value="unchecked">Não Corrigidos</option>
              </select>
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {invoices.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {invoices.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalInvoices}</div>
                <div className="card-label">Total de Boletins</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.checkedInvoices}</div>
                <div className="card-label">Corrigidos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.paidInvoices}</div>
                <div className="card-label">Pagos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.totalValue)}</div>
                <div className="card-label">Valor Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.paidValue)}</div>
                <div className="card-label">Valor Pago</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.pendingValue)}</div>
                <div className="card-label">Valor Pendente</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {invoices.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({filteredInvoices.length} boletins)</h3>
            </div>

            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Equipe</th>
                    <th>Projeto</th>
                    <th>Título</th>
                    <th>PMS</th>
                    <th>OS</th>
                    <th>Município</th>
                    <th>Data</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.key}>
                      <td>{invoice.id}</td>
                      <td>{invoice.team}</td>
                      <td className="text-cell" title={invoice.project}>{invoice.project}</td>
                      <td className="text-cell" title={invoice.title}>{invoice.title}</td>
                      <td className="text-cell" title={invoice.pms}>{invoice.pms}</td>
                      <td className="text-cell" title={invoice.os}>{invoice.os}</td>
                      <td className="text-cell" title={invoice.city}>{invoice.city}</td>
                      <td>{formatDate(invoice.date)}</td>
                      <td className="value-cell">{formatCurrency(invoice.totalWithFactor || 0)}</td>
                      <td>{getStatusBadge(invoice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredInvoices.length === 0 && invoices.length > 0 && (
              <div className="empty-results">
                <i className="fas fa-search"></i>
                <p>Nenhum boletim encontrado com os filtros aplicados.</p>
              </div>
            )}
          </div>
        )}

        {invoices.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-chart-line"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Configure os filtros e clique em "Gerar Relatório" para visualizar os dados.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralReport;
